<template>
  <div class="products-wrapper">
    <h4 class="d-flex mb-3">
      <span>{{ $t('purchase.productsToOrder') }}</span>

      <d-button
        v-if="!readOnly"
        @click.prevent="addProduct"
        theme="success"
        class="ml-auto">
        <i class="material-icons">add</i>
        {{ $t('product.addProduct') }}
      </d-button>
    </h4>

    <d-card
      v-for="(productData, index) of products"
      :key="'purchase-product-' + index"
      class="mb-4">
      <table class="table mb-0">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0 d-flex">{{ $t('product.productName') }}</th>
            <th scope="col" class="border-0" style="width: 140px;">{{ $t('ui.amount') }}</th>
            <th scope="col" class="border-0" style="width: 140px;">{{ $t('price.priceNet') }}</th>
            <th scope="col" class="border-0" style="width: 140px;">{{ $t('price.vatRate') }}</th>
            <th scope="col" class="border-0" style="width: 200px;">{{ $t('price.bruttonPrice') }}</th>
            <th scope="col" class="border-0" style="width: 80px;" v-if="!readOnly">{{ $t('ui.delete') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="align-top d-flex is-v-select-wrapper flex-wrap">
              <div class="d-flex w-100">
                <validation-provider
                  rules="required"
                  :name="'product-id-' + index"
                  tag="div"
                  class="w-100"
                  v-slot="{ errors }">
                  <div :class="{ 'form-control w-100': true, 'is-select': true, 'is-invalid': errors.length }">
                    <v-select
                      v-if="!readOnly"
                      :options="availableProducts"
                      v-model="productData.product_id"
                      :reduce="product => product.id"
                      :filterBy="filterByForProducts"
                      :placeholder="$t('product.selectProduct')"
                      @input="productChanged(index)"
                      label="code">
                      <template slot="no-options">
                        {{ $t('product.noProductsMatchingGivenPhrase') }}&hellip;
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.code }} - {{ option.name }}
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.code }} - {{ option.name }}
                        </div>
                      </template>
                    </v-select>
                    <span class="p-2 d-block" v-else>
                      {{ productData.product_name }}
                    </span>
                  </div>
                </validation-provider>
              </div>
            </td>
            <td class="align-top">
              <validation-provider
                rules="required|min_value:1"
                :name="'product-quantity-' + index"
                v-slot="{ errors }">
                <d-input-group>
                  <d-input
                    :min="1"
                    :state="errors.length ? 'invalid' : null"
                    v-model="productData.quantity"
                    :readonly="readOnly"
                    type="number" />
                  <d-input-group-text slot="append">{{ $t('ui.piecesAbbr') }}</d-input-group-text>
                </d-input-group>
              </validation-provider>
            </td>
            <td class="align-top">
              <validation-provider
                rules="required|min_value:0"
                :name="'product-price-net-' + index"
                v-slot="{ errors }">
                <d-input-group
                  :class="{'is-invalid': errors.length}">
                  <input
                    :class="{
                      'form-control': true,
                      'is-invalid': errors.length
                    }"
                    :invalid="errors.length ? true : null"
                    :min="0"
                    type="number"
                    v-model="productData.price_net"
                    :readonly="readOnly" />
                  <d-input-group-text slot="append">
                    {{ $t('price.pln') }}
                  </d-input-group-text>
                </d-input-group>
                <d-form-invalid-feedback>
                  {{ errors[0] }}
                </d-form-invalid-feedback>

                <small
                  v-if="productData.product_id && productPrices[productData.product_id] && !readOnly"
                  class="text-primary cursor-pointer"
                  @click="setProductPriceFromPricing(index)">
                  {{ $t('price.pricesList') }}: {{ productPrices[productData.product_id] }} PLN<br>
                </small>
              </validation-provider>
            </td>
            <td class="align-top">
              <validation-provider
                rules="required"
                :name="'product-vat-rate-' + index"
                v-slot="{ errors }">
                <d-form-select
                  v-if="!readOnly"
                  v-model="productData.vat_rate"
                  :state="errors.length ? 'invalid' : null"
                  :options="[
                    { value: '23.00', text: '23%' },
                    { value: '8.00', text: '8%' },
                    { value: '5.00', text: '5%' },
                    { value: '0.00', text: '0%' }
                  ]"
                />
                <span class="p-1 d-block" v-else>
                  {{ productData.vat_rate }}%
                </span>
              </validation-provider>
            </td>
            <td class="align-top">
              {{ getProductGrossPrice(productData) }}<br>
              <small class="text-primary">{{ $t('purchase.contains') }} {{ getProductVAT(productData) }} {{ $t('price.vat') }}</small>
            </td>
            <td
              v-if="!readOnly"
              class="align-top">
              <d-button
                outline
                @click.prevent="removeProduct(index, productData.can_delete)"
                theme="danger">
                {{ $t('ui.delete') }}
              </d-button>
            </td>
          </tr>

          <tr v-if="!readOnly && useSewingRoomIntegration && productData.product_id">
            <td colspan="6">
              <div class="d-flex w-100">
                <d-checkbox
                  inline
                  v-model="productData.pattern_required"
                  toggle
                  toggle-small>
                  <small>{{ $t('purchase.requiresShape') }}</small>
                </d-checkbox>
                <d-checkbox
                  inline
                  v-model="productData.pattern_stays"
                  toggle
                  toggle-small>
                  <small>{{ $t('purchase.shapeStaysInHafciarnia') }}</small>
                </d-checkbox>
              </div>

              <div class="d-flex w-100">
                <div class="w-25 pr-1">
                  <label>
                    <small>{{ $t('purchase.size.size') }}:</small>
                  </label>

                  <div :class="{ 'form-control': true, 'is-select': true }">
                    <v-select
                      :options="getSizeForProduct(productData.product_id)"
                      v-model="productData.size_id"
                      :placeholder="$t('purchase.size.selectSize')"
                      :reduce="size => size.value"
                      label="text"
                      :taggable="true"
                      @option:created="productToAddSewingRoomItems = index; addNewSizeToSewingRoom($event, productData.product_id)">
                      <template slot="no-options">
                        {{ $t('purchase.size.noSuchSize') }}&hellip;
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.text }}
                          <template v-if="isNewOption('sizes', option.text, index)">
                            ({{ $t('ui.addNew') }})
                          </template>
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.text }}
                        </div>
                      </template>
                    </v-select>
                  </div>
                </div>

                <div class="w-25 pl-1 pr-1">
                  <label>
                    <small>{{ $t('purchase.fabric.fabric') }}:</small>
                  </label>

                  <div :class="{ 'form-control': true, 'is-select': true }">
                    <v-select
                      :options="availableMaterials"
                      v-model="productData.material_id"
                      :placeholder="$t('purchase.fabric.chooseFabric')"
                      label="text"
                      :reduce="material => material.value"
                      :taggable="true"
                      @option:created="productToAddSewingRoomItems = index; addNewMaterialToSewingRoom($event)">
                      <template slot="no-options">
                        {{ $t('purchase.fabric.noSuchFabric') }}&hellip;
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.text }}
                          <template v-if="isNewOption('materials', option.text)">
                            ({{ $t('ui.addNew') }}
                          </template>
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.text }}
                        </div>
                      </template>
                    </v-select>
                  </div>
                </div>

                <div class="w-25 pl-1 pr-1">
                  <label>
                    <small>{{ $t('purchase.fabric.fabricColor') }}:</small>
                  </label>

                  <div :class="{ 'form-control': true, 'is-select': true }">
                    <v-select
                      :options="getColorsForMaterial(productData.material_id)"
                      v-model="productData.color_id"
                      :disabled="!productData.material_id"
                      :placeholder="$t('purchase.fabric.chooseColor')"
                      label="text"
                      :reduce="color => color.value"
                      :taggable="true"
                      @option:created="productToAddSewingRoomItems = index; addNewColorToSewingRoom($event)">
                      <template slot="no-options">
                        {{ $t('purchase.fabric.noSuchColor') }}&hellip;
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.text }}
                          <template v-if="isNewOption('colors', option.text, index)">
                            ({{ $t('ui.addNew') }})
                          </template>
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.text }}
                        </div>
                      </template>
                    </v-select>
                  </div>
                </div>

                <div class="w-25 pl-1 pr-1">
                  <label>
                    <small>{{ $t('purchase.fleece.fleece') }}:</small>
                  </label>

                  <div :class="{ 'form-control': true, 'is-select': true }">
                    <v-select
                      :options="availablePolars"
                      v-model="productData.polar_id"
                      :placeholder="$t('purchase.fleece.chooseFleece')"
                      label="text"
                      :reduce="polar => polar.value"
                      :taggable="true"
                      @option:created="productToAddSewingRoomItems = index; addNewPolarToSewingRoom($event)">
                      <template slot="no-options">
                        {{ $t('purchase.fleece.noSuchFleece') }}&hellip;
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.text }}
                          <template v-if="isNewOption('polars', option.text)">
                            ({{ $t('ui.addNew') }})
                          </template>
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.text }}
                        </div>
                      </template>
                    </v-select>
                  </div>
                </div>

                <div class="w-25 pl-1">
                  <label>
                    <small>{{ $t('purchase.stitching.stitching') }}:</small>
                  </label>
                  <div :class="{ 'form-control': true, 'is-select': true }">
                    <v-select
                      :options="availableSeams"
                      v-model="productData.seam_id"
                      :placeholder="$t('purchase.stitching.chooseStitching')"
                      label="text"
                      :reduce="seam => seam.value"
                      :taggable="true"
                      @option:created="productToAddSewingRoomItems = index; addNewSeamToSewingRoom($event)">
                      <template slot="no-options">
                        {{ $t('purchase.stitching.noSuchStitching') }}&hellip;
                      </template>
                      <template slot="option" slot-scope="option">
                        <div class="d-center">
                          {{ option.text }}
                          <template v-if="isNewOption('seams', option.text)">
                            ({{ $t('purchase.addNew') }})
                          </template>
                        </div>
                      </template>
                      <template slot="selected-option" slot-scope="option">
                        <div class="selected d-center">
                          {{ option.text }}
                        </div>
                      </template>
                    </v-select>
                  </div>
                </div>
              </div>
            </td>
          </tr>

          <tr v-if="readOnly && productData.product_id">
            <td colspan="6">
              <div class="d-flex w-100">
                <small>{{ $t('purchase.requiresShape') }}: </small>

                <small
                  v-if="productData.pattern_required"
                  class="text-success">
                  {{ $t('ui.yes') }}
                </small>
                <small
                  v-else
                  class="text-danger">
                  {{ $t('ui.no') }}
                </small>
              </div>
              <div class="d-flex w-100">
                <small>{{ $t('purchase.shapeStaysInHafciarnia') }}: </small>

                <small
                  v-if="productData.pattern_stays"
                  class="text-success">
                  {{ $t('ui.yes') }}
                </small>
                <small
                  v-else
                  class="text-danger">
                  {{ $t('ui.no') }}
                </small>
              </div>

              <div
                v-if="productData.size_name || productData.material_name || productData.color_name || productData.seam_name || productData.polar_name"
                class="d-flex w-100">
                <div
                  v-if="productData.size_name"
                  class="w-25 pr-1">
                  <label>
                    <small>{{ $t('purchase.size.size') }}:</small>
                  </label>

                  <small :class="{
                    'form-control': true,
                    'text-primary': true
                  }">
                    {{ productData.size_name }}
                  </small>
                </div>

                <div
                  v-if="productData.material_name"
                  class="w-25 pl-1 pr-1">
                  <label>
                    <small>{{ $t('purchase.fabric.fabric') }}:</small>
                  </label>

                  <small :class="{
                    'form-control': true,
                    'text-primary': true
                  }">
                    {{ productData.material_name }}
                  </small>
                </div>

                <div
                  v-if="productData.color_name"
                  class="w-25 pl-1 pr-1">
                  <label>
                    <small>{{ $t('purchase.fabric.fabricColor') }}:</small>
                  </label>

                  <small :class="{
                    'form-control': true,
                    'text-primary': true
                  }">
                    {{ productData.color_name }}
                  </small>
                </div>

                <div
                  v-if="productData.polar_name"
                  class="w-25 pl-1 pr-1">
                  <label>
                    <small>{{ $t('purchase.fleece.fleece') }}:</small>
                  </label>

                  <small :class="{
                    'form-control': true,
                    'text-primary': true
                  }">
                    {{ productData.polar_name }}
                  </small>
                </div>

                <div
                  v-if="productData.seam_name"
                  class="w-25 pl-1">
                  <label>
                    <small>{{ $t('purchase.stitching.stitching') }}:</small>
                  </label>
                  <small :class="{
                    'form-control': true,
                    'text-primary': true
                  }">
                    {{ productData.seam_name }}
                  </small>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </d-card>

    <div
      v-if="products.length"
      class="text-right">
      <h4 class="mt-5 mb-0">{{ totalNet }} {{ $t('price.plnNet') }}</h4>
      {{ totalGross }} {{ $t('price.plnBrutto') }}<br>
      <span class="text-primary">
        {{ totalVAT }} {{ $t('price.plnVat') }}
      </span>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import CustomDecimals from './../../utils/CustomDecimals.js';
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'purchase-creator',
  props: [
    'availableProducts',
    'productProperties',
    'productPrices',
    'purchaseSetUpInProgress',
    'readOnly',
    'status',
    'useSewingRoomIntegration'
  ],
  computed: {
    totalNet () {
      return CustomDecimals.roundProperly(this.products.reduce((sum, product) => sum + parseFloat(product.price_net * product.quantity), 0));
    },
    totalGross () {
      return CustomDecimals.roundProperly(this.products.reduce((sum, product) => {
        let vatSuffix = parseInt(product.vat_rate, 10) < 10 ? '0' + product.vat_rate : product.vat_rate;
        return sum + (parseFloat(CustomDecimals.roundProperly(parseFloat(product.price_net) * parseFloat('1.' + vatSuffix) * product.quantity)));
      }, 0));
    },
    totalVAT () {
      return CustomDecimals.roundProperly(parseFloat(this.totalGross) - parseFloat(this.totalNet));
    },
    availableMaterials () {
      if (!this.productProperties || !this.productProperties.materials) {
        return [];
      }

      return this.productProperties.materials.map(material => ({
        text: material.name,
        value: material.id
      }));
    },
    availablePolars () {
      if (!this.productProperties || !this.productProperties.polars) {
        return [];
      }

      return this.productProperties.polars.map(polar => ({
        text: polar.name,
        value: polar.id
      }));
    },
    availableSeams () {
      if (!this.productProperties || !this.productProperties.seams) {
        return [];
      }

      return this.productProperties.seams.map(seam => ({
        text: seam.name,
        value: seam.id
      }));
    },
    availableMaterialNames () {
      if (this.productProperties && this.productProperties.materials) {
        return this.productProperties.materials.map(material => material.name);
      }

      return [];
    },
    availablePolarNames () {
      if (this.productProperties && this.productProperties.polars) {
        return this.productProperties.polars.map(polar => polar.name);
      }

      return [];
    },
    availableSeamNames () {
      if (this.productProperties && this.productProperties.seams) {
        return this.productProperties.seams.map(seam => seam.name);
      }

      return [];
    }
  },
  data () {
    return {
      productToAddSewingRoomItems: null,
      products: []
    };
  },
  methods: {
    prepareProductsData () {
      return this.products.map(product => {
        let vatSuffix = parseInt(product.vat_rate, 10) < 10 ? '0' + product.vat_rate : product.vat_rate;
        let priceVAT = CustomDecimals.roundProperly((product.price_net * parseFloat('1.' + vatSuffix)) - product.price_net);
        let priceGross = CustomDecimals.roundProperly(product.price_net * parseFloat('1.' + vatSuffix));

        let productObject = {
          id: product.id,
          product_id: product.product_id,
          quantity: product.quantity,
          price_net: CustomDecimals.roundProperly(product.price_net),
          vat_rate: product.vat_rate,
          price_vat: priceVAT,
          price_gross: priceGross
        };

        if (this.useSewingRoomIntegration) {
          productObject.pattern_required = +product.pattern_required;
          productObject.pattern_stays = +product.pattern_stays;
          productObject.size_id = product.size_id;
          productObject.material_id = product.material_id;
          productObject.color_id = product.color_id;
          productObject.polar_id = product.polar_id;
          productObject.seam_id = product.seam_id;
        }

        return productObject;
      });
    },
    setUpProducts (products) {
      for (let i = 0; i < products.length; i++) {
        products[i].pattern_required = !!products[i].pattern_required;
        products[i].pattern_stays = !!products[i].pattern_stays;
      }

      Vue.set(this, 'products', products);
    },
    addProduct () {
      this.products.push({
        id: null,
        product_id: null,
        quantity: 1,
        price_net: 0,
        vat_rate: '23.00',
        price_vat: 0,
        price_gross: 0,
        pattern_required: false,
        pattern_stays: false,
        size_id: 0,
        material_id: 0,
        color_id: 0,
        polar_id: 0,
        seam_id: 0,
        can_delete: true
      });
    },
    removeProduct(index, canDelete) {
      if (!canDelete) {
        Vue.swal({
          title: this.$t('order.exisitngProductsInHplOrder'),
          html: this.$t('purchase.removeProductText'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        return;
      }

      this.products.splice(index, 1);
    },
    filterByForProducts (option, label, search) {
      return (option.code + option.name).toLowerCase().indexOf(search.toLowerCase()) > -1;
    },
    getProductGrossPrice (product) {
      let vatSuffix = parseInt(product.vat_rate, 10) < 10 ? '0' + product.vat_rate : product.vat_rate;
      return CustomDecimals.roundProperly(parseFloat(CustomDecimals.roundProperly(product.price_net * parseFloat('1.' + vatSuffix) * product.quantity))) + ' ' + this.$t('price.pln');
    },
    getProductVAT (product) {
      let vatSuffix = parseInt(product.vat_rate, 10) < 10 ? '0' + product.vat_rate : product.vat_rate;
      return CustomDecimals.roundProperly(parseFloat(CustomDecimals.roundProperly(((product.price_net * parseFloat('1.' + vatSuffix)) - product.price_net) * product.quantity))) + ' ' + this.$t('price.pln');
    },
    getProductNameById (productID) {
      let foundedProducts = this.availableProducts.filter(product => product.id === productID);

      if (foundedProducts.length) {
        return foundedProducts[0].code + ' - ' + foundedProducts[0].name;
      }

      return '-';
    },
    getSizeForProduct (productID) {
      productID = parseInt(productID, 10);

      if (this.productProperties && this.productProperties.products_sizes && this.productProperties.products_sizes[productID]) {
        return this.productProperties.products_sizes[productID].map(size => ({
          text: size.name,
          value: size.id
        }));
      }

      return [];
    },
    getColorsForMaterial (materialID) {
      materialID = parseInt(materialID, 10);

      if (this.productProperties && this.productProperties.materials) {
        let foundedMaterial = this.productProperties.materials.filter(material => material.id === materialID);

        if (foundedMaterial[0]) {
          return foundedMaterial[0].colors.map(color => ({
            text: color.name,
            value: color.id
          }));
        }

        return [];
      }

      return [];
    },
    productChanged (index) {
      Vue.set(this.products[index], 'pattern_required', false);
      Vue.set(this.products[index], 'pattern_stays', false);
      Vue.set(this.products[index], 'size_id', 0);
      Vue.set(this.products[index], 'material_id', 0);
      Vue.set(this.products[index], 'color_id', 0);
      Vue.set(this.products[index], 'polar_id', 0);
      Vue.set(this.products[index], 'seam_id', 0);

      let productID = this.products[index].product_id;
      if (typeof this.productPrices[productID] !== 'undefined') {
        let productPrice = this.productPrices[productID];
        Vue.set(this.products[index], 'price_net', productPrice);
      } else {
        Vue.set(this.products[index], 'price_net', 0);
      }
    },
    addNewMaterialToSewingRoom (newMaterial) {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/sewing-room/store-material',
        formData: {
          name: newMaterial.text
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        skipErrorDisplay: true,
        successAction: (response) => {
          if (response.success) {
            if (!response.item.colors) {
              response.item.colors = [];
            }

            this.$bus.$emit('purchase-reload-product-properties');
            this.products[this.productToAddSewingRoomItems].material_id = response.item.id;

            Vue.swal({
              title: this.$t('purchase.fabric.addFabricSuccessTitle'),
              html: this.$t('purchase.fabric.addFabricSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true,
              timer: 2000
            });
          } else {
            this.products[this.productToAddSewingRoomItems].material_id = null;

            Vue.swal({
              title: this.$t('purchase.fabric.addFabricErrorText'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.products[[this.productToAddSewingRoomItems]].material_id = null;

          Vue.swal({
            title: this.$t('purchase.fabric.addFabricErrorText'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      });
    },
    addNewColorToSewingRoom (newColor) {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/sewing-room/store-color',
        formData: {
          name: newColor.text,
          material_id: this.products[this.productToAddSewingRoomItems].material_id
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        skipErrorDisplay: true,
        successAction: (response) => {
          if (response.success) {
            this.$bus.$emit('purchase-reload-product-properties');
            this.products[this.productToAddSewingRoomItems].color_id = response.item.id;

            Vue.swal({
              title: this.$t('purchase.fabric.addFabricColorSuccessTitle'),
              html: this.$t('purchase.fabric.addFabricColorSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true,
              timer: 2000
            });
          } else {
            this.products[this.productToAddSewingRoomItems].color_id = null;

            Vue.swal({
              title: this.$t('purchase.fabric.addFabricColorErrorText'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.products[this.productToAddSewingRoomItems].color_id = null;

          Vue.swal({
            title: this.$t('purchase.fabric.addFabricColorErrorText'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      });
    },
    addNewSizeToSewingRoom (newSize, productID) {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/sewing-room/store-size',
        formData: {
          name: newSize && newSize.text ? newSize.text : newSize,
          product_id: productID
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        skipErrorDisplay: true,
        successAction: (response) => {
          if (response.success) {
            this.$bus.$emit('purchase-reload-product-properties');
            this.products[this.productToAddSewingRoomItems].size_id = response.item.id;

            Vue.swal({
              title: this.$t('purchase.size.addSizeSuccessTitle'),
              html: this.$t('purchase.size.addSizeSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true,
              timer: 2000
            });
          } else {
            this.products[this.productToAddSewingRoomItems].size_id = null;

            Vue.swal({
              title: this.$t('purchase.size.addSizeErrorText'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.products[this.productToAddSewingRoomItems].size_id = null;

          Vue.swal({
            title: this.$t('purchase.size.addSizeErrorText'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      });
    },
    addNewSeamToSewingRoom (newSeam) {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/sewing-room/store-seam',
        formData: {
          name: newSeam.text
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        skipErrorDisplay: true,
        successAction: (response) => {
          if (response.success) {
            this.$bus.$emit('purchase-reload-product-properties');
            this.products[this.productToAddSewingRoomItems].seam_id = response.item.id;

            Vue.swal({
              title: this.$t('purchase.stitching.addStitchingSuccessTitle'),
              html: this.$t('purchase.stitching.addStitchingSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true,
              timer: 2000
            });
          } else {
            this.products[this.productToAddSewingRoomItems].seam_id = null;

            Vue.swal({
              title: this.$t('purchase.stitching.addStitchingErrorText'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.products[this.productToAddSewingRoomItems].seam_id = null;

          Vue.swal({
            title: this.$t('purchase.stitching.addStitchingErrorText'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      });
    },
    addNewPolarToSewingRoom (newPolar) {
      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/sewing-room/store-polar',
        formData: {
          name: newPolar.text
        },
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('ui.actionFailedTryAgain'),
        skipErrorDisplay: true,
        successAction: (response) => {
          if (response.success) {
            this.$bus.$emit('purchase-reload-product-properties');
            this.products[this.productToAddSewingRoomItems].polar_id = response.item.id;

            Vue.swal({
              title: this.$t('purchase.fleece.addFleeceSuccessTitle'),
              html: this.$t('purchase.fleece.addFleeceSuccessText'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true,
              timer: 2000
            });
          } else {
            this.products[this.productToAddSewingRoomItems].polar_id = null;

            Vue.swal({
              title: this.$t('purchase.fleece.addFleeceErrorText'),
              html: this.$t('ui.tryAgain'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        },
        failAction: () => {
          this.products[this.productToAddSewingRoomItems].polar_id = null;

          Vue.swal({
            title: this.$t('purchase.fleece.addFleeceErrorText'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      });
    },
    isNewOption (type, valueToCheck, productIndex = null) {
      if (type === 'materials') {
        return this.availableMaterialNames.indexOf(valueToCheck) === -1;
      } else if (type === 'polars') {
        return this.availablePolarNames.indexOf(valueToCheck) === -1;
      } else if (type === 'seams') {
        return this.availableSeamNames.indexOf(valueToCheck) === -1;
      } else if (type === 'colors') {
        return this.availableColorNames(productIndex).indexOf(valueToCheck) === -1;
      } else if (type === 'sizes') {
        return this.availableSizeNames(productIndex).indexOf(valueToCheck) === -1;
      }

      return false;
    },
    availableColorNames (productIndex) {
      if (this.productProperties && this.productProperties.materials && this.products[productIndex].material_id) {
        let foundedMaterial = this.productProperties.materials.filter(material => material.id === this.products[productIndex].material_id);

        if (foundedMaterial[0]) {
          return foundedMaterial[0].colors.map(color => color.name);
        }

        return [];
      }

      return [];
    },
    availableSizeNames (productIndex) {
      let productID = this.products[productIndex].product_id;

      if (this.productProperties && this.productProperties.products_sizes && this.productProperties.products_sizes[productID]) {
        return this.productProperties.products_sizes[productID].map(size => size.name);
      }

      return [];
    },
    setProductPriceFromPricing (index) {
      Vue.set(this.products[index], 'price_net', this.productPrices[this.products[index].product_id]);
    }
  }
};
</script>

<style lang="scss">
.cursor-pointer {
  cursor: pointer;
}
</style>
